import { useCallback } from "react";
import { Box, StackLayout, HeadingText } from "@leagueplatform/genesis-core";
import { useDocumentTitle } from "@leagueplatform/web-common";
import { useIntl } from "@leagueplatform/locales";
import { LoadingIndicator } from "@leagueplatform/web-common-components";
import {
  PRODUCT_AREA,
  SCREEN_NAMES,
  usePageViewAnalytics,
} from "@leagueplatform/analytics";
import { useProfileData } from "hooks/use-profile-data.hook";
import { useIsBelowTablet } from "hooks/use-is-below-tablet.hook";
import { useUpdatePersonalInfo } from "hooks/mutations/use-update-personal-info.hook";
import { useUpdateContactInfo } from "hooks/mutations/use-update-contact-info.hook";
import { useUpdateMailingAddress } from "hooks/mutations/use-update-mailing-address.hook";
import { useUpdateCommunicationPreferences } from "hooks/mutations/use-update-communication-preferences.hook";
import { useUpdateUserProfile } from "hooks/mutations/use-update-user-profile.hook";
import {
  PersonalInfoSection,
  type PersonalInfoFormValues,
} from "components/forms/personal-info-section/personal-info-section.component";
import {
  MailingAddressSection,
  type MailingAddressFormValues,
} from "components/forms/mailing-address-section/mailing-address-section.component";
import {
  ContactInfoSection,
  type ContactInfoFormValues,
} from "components/forms/contact-info-section/contact-info-section.component";
import {
  CommunicationsSection,
  type CommunicationPreferencesValues,
} from "components/forms/communications-section/communications-section.component";
import { ExitWarningContextProvider } from "components/modals/exit-warning/exit-warning.context";
import { ExitWarning } from "components/modals/exit-warning/exit-warning.component";
import { useExitWarningNavigationBlock } from "hooks/use-exit-warning-navigation-block.hook";
import { useGetCommunicationPreferences } from "hooks/queries/use-get-communication-preferences.hook";
import { useUpdateTextNotifications } from "hooks/mutations/use-update-text-notifications";

export const CONTENT_MAX_WIDTH = 952;

const PersonalInformationInner = () => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useIsBelowTablet();
  const { data, isLoading } = useProfileData();
  const { updatePersonalInfo } = useUpdatePersonalInfo();
  const { updateContactInfo } = useUpdateContactInfo();
  const { updateMailingAddress } = useUpdateMailingAddress();
  const { updateUserProfile } = useUpdateUserProfile();
  const { updateTextNotifications } = useUpdateTextNotifications();

  const { data: communicationPreferencesData } =
    useGetCommunicationPreferences();
  const { updateCommunicationPreferences } =
    useUpdateCommunicationPreferences();

  useDocumentTitle(formatMessage({ id: "STR_EDIT_PERSONAL_INFORMATION" }));
  usePageViewAnalytics({
    product_area: PRODUCT_AREA.SETTINGS,
    screen_name: SCREEN_NAMES.PERSONAL_INFO,
  });

  const { state, proceed, reset } = useExitWarningNavigationBlock();

  const handleClose = useCallback(() => {
    reset?.();
  }, [reset]);

  const handleProceed = useCallback(() => {
    proceed?.();
  }, [proceed]);

  const handlePersonalInfoSubmit = useCallback(
    async (values: PersonalInfoFormValues) => {
      await updatePersonalInfo(values);
    },
    [updatePersonalInfo]
  );

  const handleMailingAddressSubmit = useCallback(
    async (values: MailingAddressFormValues) => {
      await updateMailingAddress(values);
    },
    [updateMailingAddress]
  );

  const handleContactInfoSubmit = useCallback(
    async (values: ContactInfoFormValues) => {
      await updateContactInfo(values);
    },
    [updateContactInfo]
  );

  const handleCommunicationPreferencesUpdate = useCallback(
    async (values: CommunicationPreferencesValues) => {
      const {
        mobilePhoneNumber,
        emailNotification,
        textNotification,
        paperReports,
      } = values;

      await updateTextNotifications(mobilePhoneNumber, !textNotification);

      const profileEmail = data?.optedIntoMarketingCommunications;

      if (emailNotification !== profileEmail) {
        await updateUserProfile({
          opted_into_marketing_communications: emailNotification,
        });
      }

      await updateCommunicationPreferences({
        emailNotification,
        textNotification,
        paperReports,
        mobilePhoneNumber,
      });
    },
    [
      data,
      updateUserProfile,
      updateCommunicationPreferences,
      updateTextNotifications,
    ]
  );

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <Box
        css={{
          backgroundColor: "$surfaceBackgroundTertiary",
          height: isBelowTablet ? "auto" : 140,
          paddingInline: "$two",
          paddingBlock: isBelowTablet ? "$two" : "$none",
        }}
      >
        <StackLayout
          orientation="horizontal"
          verticalAlignment="center"
          css={{
            maxWidth: CONTENT_MAX_WIDTH,
            marginInline: "auto",
            height: "100%",
          }}
        >
          <HeadingText level="1" size={isBelowTablet ? "xl" : "xxl"}>
            {formatMessage({ id: "STR_EDIT_PERSONAL_INFORMATION" })}
          </HeadingText>
        </StackLayout>
      </Box>

      {/* Main Content */}
      <Box
        css={{
          maxWidth: CONTENT_MAX_WIDTH,
          marginInline: "auto",
          marginBlockStart: "$two",
          marginBlockEnd: "$five",
          paddingInline: isBelowTablet ? "$oneAndHalf" : "$two",
        }}
      >
        <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
          <PersonalInfoSection
            data={data}
            onSubmit={handlePersonalInfoSubmit}
          />
          <MailingAddressSection
            data={data}
            onSubmit={handleMailingAddressSubmit}
          />
          <ContactInfoSection data={data} onSubmit={handleContactInfoSubmit} />
          {communicationPreferencesData && (
            <CommunicationsSection
              data={communicationPreferencesData}
              mobilePhoneNumber={data?.mobilePhoneNumber}
              onUpdatePreferences={handleCommunicationPreferencesUpdate}
            />
          )}
        </StackLayout>
      </Box>

      {/* Exit Warning Modal for unsaved changes */}
      <ExitWarning
        open={state === "blocked"}
        onStay={handleClose}
        onProceed={handleProceed}
      />
    </>
  );
};

export const PersonalInformation = () => (
  <ExitWarningContextProvider>
    <PersonalInformationInner />
  </ExitWarningContextProvider>
);
